/**
 * Shipping Calculator - Theoric
 * Calls N8N webhook and supports multiple shipping options.
 */
(function () {
  'use strict';

  const N8N_ENDPOINT = 'https://lab.theoric.com.br/webhook/FRECALC';
  const STORAGE_KEY = 'theoric_shipping_cep';
  const DEBUG_STORAGE_KEY = 'theoric_shipping_debug';
  const DEBUG_QUERY_KEY = 'shipping_debug';
  const DEFAULT_FREE_SHIPPING_THRESHOLD_CENTS = 31900;

  function formatBusinessDays(days) {
    const parsedDays = Number(days);
    if (!Number.isFinite(parsedDays) || parsedDays < 1) return '';

    const safeDays = Math.round(parsedDays);
    return safeDays === 1 ? '1 dia \u00fatil' : safeDays + ' dias \u00fateis';
  }

  function formatCurrency(value) {
    return 'R$ ' + Number(value || 0).toFixed(2).replace('.', ',');
  }

  function maskCEP(value) {
    const digits = String(value || '').replace(/\D/g, '').slice(0, 8);
    if (digits.length > 5) return digits.slice(0, 5) + '-' + digits.slice(5);
    return digits;
  }

  function parseFlexibleNumber(value) {
    if (value === undefined || value === null || value === '') return null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    const parsed = Number(String(value).trim().replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : null;
  }

  function toNumber(value, fallback) {
    const parsed = parseFlexibleNumber(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function toPositiveNumber(value, fallback) {
    const parsed = parseFlexibleNumber(value);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
    return fallback;
  }

  function toMoneyFromCents(valueInCents) {
    return formatCurrency(toNumber(valueInCents, 0) / 100);
  }

  function isDebugEnabled() {
    try {
      const params = new URLSearchParams(window.location.search || '');
      if (params.get(DEBUG_QUERY_KEY) === '1') return true;
    } catch (e) {
      // no-op
    }

    try {
      return localStorage.getItem(DEBUG_STORAGE_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function readShippingCatalogData(sectionRoot, productId) {
    const emptyCatalog = { variants: {}, defaults: {} };
    if (!sectionRoot) return emptyCatalog;

    const script =
      sectionRoot.querySelector('#TheoricShippingCatalog-' + productId) ||
      sectionRoot.querySelector('[data-shipping-catalog-json]');

    if (!script) return emptyCatalog;

    try {
      const parsed = JSON.parse(script.textContent || '{}');
      const variants = parsed && typeof parsed.variants === 'object' ? parsed.variants : {};
      const defaults = parsed && typeof parsed.defaults === 'object' ? parsed.defaults : {};
      return { variants, defaults };
    } catch (e) {
      return emptyCatalog;
    }
  }

  function mapShippingLabel(rawLabel, serviceCode) {
    const cleanLabel = String(rawLabel || '').trim();
    const normalizedLabel = cleanLabel.toLowerCase();
    const normalizedService = String(serviceCode || '').toLowerCase();
    const isGenericLabel = !cleanLabel || ['b2c', 'delivery', 'entrega', 'frete', 'shipping'].includes(normalizedLabel);
    const isPackslog = normalizedLabel.includes('packslog') || normalizedService.includes('packslog');
    const isCorreios =
      normalizedLabel.includes('correios') ||
      normalizedLabel.includes('sedex') ||
      normalizedService.includes('sedex') ||
      /\bpac\b/.test(normalizedLabel) ||
      /\bpac\b/.test(normalizedService);
    const isTotalExpress =
      /total[\s_-]?express/.test(normalizedLabel) || /total[\s_-]?express/.test(normalizedService);
    const isExpress =
      normalizedService.includes('sameday') ||
      normalizedService.includes('express') ||
      normalizedLabel.includes('same day') ||
      normalizedLabel.includes('sameday') ||
      normalizedLabel.includes('express');

    if (isCorreios && !isGenericLabel) return cleanLabel;
    if (isTotalExpress) return 'Total Express';
    if (isExpress) return 'Theoric Express';
    if (isPackslog) return 'Theoric Essencial';
    if (!isGenericLabel) return cleanLabel;
    return 'Theoric Essencial';
  }

  function getShippingResponseEntries(data) {
    return Array.isArray(data) ? data : [data];
  }

  function normalizeOptions(data) {
    const rawOptions = [];

    getShippingResponseEntries(data).forEach(function (entry) {
      if (Array.isArray(entry?.options)) rawOptions.push(...entry.options);
      if (Array.isArray(entry?.shipping_options)) rawOptions.push(...entry.shipping_options);
      if (Array.isArray(entry?.rates)) rawOptions.push(...entry.rates);
      if (Array.isArray(entry?.data?.options)) rawOptions.push(...entry.data.options);
      if (Array.isArray(entry?.data?.rates)) rawOptions.push(...entry.data.rates);
    });

    return rawOptions
      .map(function (opt) {
        const rawLabel = opt.label || opt.name || opt.title || 'Entrega';
        const serviceCode = opt.service || opt.service_code || opt.code || '';
        const days =
          opt.days ??
          opt.delivery_days ??
          opt.deliveryDays ??
          opt.days_to_deliver ??
          opt.deliveryTime ??
          opt.delivery_time ??
          opt.deadline ??
          0;
        const price = toNumber(opt.price ?? opt.value ?? opt.cost ?? opt.amount, 0);
        const originalPrice = toNumber(
          opt.original_price ??
            opt.originalPrice ??
            opt.price_before_discount ??
            opt.priceBeforeDiscount ??
            opt.full_price ??
            opt.fullPrice ??
            opt.compare_at_price ??
            opt.compareAtPrice ??
            price,
          price
        );
        return {
          label: mapShippingLabel(String(rawLabel), serviceCode),
          days: toNumber(days, 0),
          price: price,
          originalPrice: originalPrice > 0 ? originalPrice : price,
        };
      })
      .filter(function (opt) {
        return opt.label.length > 0;
      })
      .sort(function (a, b) {
        if (a.days !== b.days) return a.days - b.days;
        return a.price - b.price;
      });
  }

  function initCalculator(container) {
    const input = container.querySelector('.shipping-calc__input');
    const clearBtn = container.querySelector('.shipping-calc__clear-btn');
    const submitBtn = container.querySelector('.shipping-calc__submit-btn');
    const loading = container.querySelector('.shipping-calc__loading');
    const addressEl = container.querySelector('.shipping-calc__address');
    const addressText = container.querySelector('.shipping-calc__address-text');
    const changeCepBtn = container.querySelector('.shipping-calc__change-cep');
    const resultsEl = container.querySelector('.shipping-calc__results');
    const errorEl = container.querySelector('.shipping-calc__error');
    const inputWrap = container.querySelector('.shipping-calc__input-wrap');
    const editableCep = container.classList.contains('shipping-calc--editable') || !!container.querySelector('.shipping-calc__input-row');

    if (!input || !resultsEl || !errorEl) return;

    const productId = container.dataset.productId || 'default';
    const scopedStorageKey = STORAGE_KEY + '_' + productId;
    const sectionRoot = container.closest('[id^="MainProduct-"]');
    const catalogData = readShippingCatalogData(sectionRoot, productId);
    const productDefaults = {
      weightKg: toPositiveNumber(container.dataset.productWeight, toPositiveNumber(catalogData.defaults?.weightKg, null)),
      lengthCm: toPositiveNumber(container.dataset.productLength, toPositiveNumber(catalogData.defaults?.lengthCm, null)),
      widthCm: toPositiveNumber(container.dataset.productWidth, toPositiveNumber(catalogData.defaults?.widthCm, null)),
      heightCm: toPositiveNumber(container.dataset.productHeight, toPositiveNumber(catalogData.defaults?.heightCm, null)),
    };
    const debugEnabled = isDebugEnabled();
    let activeVariant = readVariantFromDom();
    const fallbackFreeShippingThresholdCents = Math.max(
      0,
      Math.round(toNumber(container.dataset.freeShippingThresholdFallbackCents, DEFAULT_FREE_SHIPPING_THRESHOLD_CENTS))
    );
    const freeShippingThresholdCents = Math.max(
      0,
      Math.round(toNumber(container.dataset.freeShippingThresholdCents, fallbackFreeShippingThresholdCents))
    );

    function debugLog(label, payload) {
      if (!debugEnabled) return;
      try {
        console.groupCollapsed('[ShippingCalc] ' + label + ' (produto ' + productId + ')');
        console.log(payload);
        console.groupEnd();
      } catch (e) {
        // no-op
      }
    }

    function renderFreeShippingIndicator() {
      const indicatorEl = container.querySelector('[data-shipping-free-indicator-text]');
      if (!indicatorEl) return;

      const fallbackText = indicatorEl.dataset.shippingFreeFallback || 'Frete gratis acima de R$ 319';
      const prefix = indicatorEl.dataset.shippingFreePrefix || 'Frete gratis acima de';

      if (freeShippingThresholdCents > 0) {
        indicatorEl.textContent = prefix + ' ' + toMoneyFromCents(freeShippingThresholdCents);
        return;
      }

      indicatorEl.textContent = fallbackText;
    }

    function getCatalogVariant(rawVariant) {
      const variantId = rawVariant?.id;
      if (!variantId) return null;
      return catalogData.variants[String(variantId)] || null;
    }

    function toCleanWeightKg(rawWeight, rawGrams) {
      const grams = toNumber(rawGrams, null);
      if (Number.isFinite(grams) && grams > 0) return Math.round((grams / 1000) * 1000) / 1000;

      const weight = toNumber(rawWeight, null);
      if (!Number.isFinite(weight) || weight <= 0) return null;

      // Shopify variant JSON usually exposes grams in weight; this keeps compatibility with legacy decimal kg.
      const normalized = weight > 20 ? weight / 1000 : weight;
      return Math.round(normalized * 1000) / 1000;
    }

    function normalizeVariant(rawVariant) {
      if (!rawVariant || typeof rawVariant !== 'object') return null;

      const catalogVariant = getCatalogVariant(rawVariant);
      const weightFromVariant = toCleanWeightKg(rawVariant.weight, rawVariant.grams);
      const weightKg = toPositiveNumber(catalogVariant?.weightKg, toPositiveNumber(weightFromVariant, productDefaults.weightKg));
      const lengthCm = toPositiveNumber(catalogVariant?.lengthCm, productDefaults.lengthCm);
      const widthCm = toPositiveNumber(catalogVariant?.widthCm, productDefaults.widthCm);
      const heightCm = toPositiveNumber(catalogVariant?.heightCm, productDefaults.heightCm);
      const unitPriceRaw = toNumber(rawVariant.price, null);
      const unitPrice = Number.isFinite(unitPriceRaw)
        ? Math.round((unitPriceRaw > 999 ? unitPriceRaw / 100 : unitPriceRaw) * 100) / 100
        : null;

      return {
        id: rawVariant.id || null,
        sku: rawVariant.sku || catalogVariant?.sku || null,
        name: rawVariant.name || rawVariant.title || null,
        weightKg,
        lengthCm,
        widthCm,
        heightCm,
        unitPrice,
      };
    }

    function readVariantFromDom() {
      const selectedVariantJson = sectionRoot?.querySelector('variant-selects [data-selected-variant]');
      if (selectedVariantJson) {
        try {
          return normalizeVariant(JSON.parse(selectedVariantJson.textContent || '{}'));
        } catch (e) {
          // keep fallbacks below
        }
      }

      const fallbackVariantId = container.dataset.productVariantId || null;
      const fallbackCatalogVariant = fallbackVariantId ? catalogData.variants[String(fallbackVariantId)] : null;
      const fallbackWeight = toCleanWeightKg(container.dataset.productWeight, container.dataset.productGrams);
      const fallbackUnitPrice = toPositiveNumber(container.dataset.productPrice, null);
      return {
        id: fallbackVariantId,
        sku: container.dataset.productVariantSku || fallbackCatalogVariant?.sku || null,
        name: container.dataset.productVariantName || document.title || null,
        weightKg: toPositiveNumber(fallbackCatalogVariant?.weightKg, toPositiveNumber(fallbackWeight, productDefaults.weightKg)),
        lengthCm: toPositiveNumber(fallbackCatalogVariant?.lengthCm, productDefaults.lengthCm),
        widthCm: toPositiveNumber(fallbackCatalogVariant?.widthCm, productDefaults.widthCm),
        heightCm: toPositiveNumber(fallbackCatalogVariant?.heightCm, productDefaults.heightCm),
        unitPrice: fallbackUnitPrice,
      };
    }

    renderFreeShippingIndicator();

    function getQuantity() {
      const quantityInput = sectionRoot?.querySelector('input[name="quantity"]');
      const quantity = parseInt(quantityInput?.value, 10);
      return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
    }

    function buildSkuEntry(data) {
      const quantity = Math.max(1, Math.round(toNumber(data?.quantity, 1)));
      const weightKg = toPositiveNumber(data?.weightKg, productDefaults.weightKg);
      const lengthCm = toPositiveNumber(data?.lengthCm, productDefaults.lengthCm);
      const widthCm = toPositiveNumber(data?.widthCm, productDefaults.widthCm);
      const heightCm = toPositiveNumber(data?.heightCm, productDefaults.heightCm);

      return {
        id: data?.id || String(productId),
        sku: data?.sku || ('SHOPIFY-' + productId),
        name: data?.name || document.title || ('Produto ' + productId),
        quantity: quantity,
        weight: Number.isFinite(weightKg) && weightKg > 0 ? Math.round(weightKg * 1000) / 1000 : null,
        length: Number.isFinite(lengthCm) && lengthCm > 0 ? Math.round(lengthCm * 100) / 100 : null,
        width: Number.isFinite(widthCm) && widthCm > 0 ? Math.round(widthCm * 100) / 100 : null,
        height: Number.isFinite(heightCm) && heightCm > 0 ? Math.round(heightCm * 100) / 100 : null,
      };
    }

    function buildPayloadFromSkus(cleanCep, skus, amount) {
      const normalizedSkus = Array.isArray(skus) ? skus.filter(Boolean) : [];
      const estimatedWeight = normalizedSkus.reduce(function (sum, sku) {
        const unitWeight = toNumber(sku?.weight, null);
        const quantity = Math.max(1, Math.round(toNumber(sku?.quantity, 1)));
        if (!Number.isFinite(unitWeight) || unitWeight <= 0) return sum;
        return sum + unitWeight * quantity;
      }, 0);

      const declaredWeight = estimatedWeight > 0 ? Math.round(estimatedWeight * 1000) / 1000 : null;
      const payload = {
        cep: cleanCep,
        zipcode: cleanCep,
        declared_weight: declaredWeight,
        weight: declaredWeight,
        skus: normalizedSkus,
      };

      if (Number.isFinite(amount) && amount > 0) {
        payload.amount = Math.round(amount * 100) / 100;
      }

      return payload;
    }

    function buildItemPayload(cleanCep, quantity) {
      const currentVariant = readVariantFromDom() || activeVariant || {};
      activeVariant = currentVariant;

      const safeQuantity = Math.max(1, Math.round(toNumber(quantity, 1)));
      const sku = buildSkuEntry({
        id: currentVariant.id,
        sku: currentVariant.sku,
        name: currentVariant.name,
        weightKg: currentVariant.weightKg,
        lengthCm: currentVariant.lengthCm,
        widthCm: currentVariant.widthCm,
        heightCm: currentVariant.heightCm,
        quantity: safeQuantity,
      });

      let amount = null;
      if (Number.isFinite(currentVariant.unitPrice) && currentVariant.unitPrice > 0) {
        amount = currentVariant.unitPrice * safeQuantity;
      }

      return buildPayloadFromSkus(cleanCep, [sku], amount);
    }

    async function requestShipping(payload) {
      const response = await fetch(N8N_ENDPOINT, {
        method: 'POST',
        // text/plain keeps this cross-origin request "simple" and avoids an
        // OPTIONS preflight on n8n installations that only register POST.
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error('Erro ao calcular frete');
      return await response.json();
    }

    function renderSubtleDisclaimer() {
      const disclaimer = document.createElement('p');
      disclaimer.className = 'shipping-calc__disclaimer';
      disclaimer.textContent = 'Valores de frete podem variar conforme os itens finais e regras ativas no checkout.';
      resultsEl.appendChild(disclaimer);
    }

    function clearAddress() {
      if (addressEl) addressEl.classList.remove('is-visible');
      if (addressText) addressText.innerHTML = '';
      container.classList.remove('shipping-calc--showing-address');
    }

    function setState(state) {
      const isInput = state === 'input';
      const isAddress = state === 'address';

      if (editableCep) {
        if (inputWrap) {
          inputWrap.style.display = 'flex';
        }
        if (addressEl) {
          addressEl.classList.remove('is-visible');
        }
        container.classList.remove('shipping-calc--showing-address');
        return;
      }

      if (inputWrap) {
        inputWrap.style.display = editableCep ? 'flex' : isInput ? 'flex' : 'none';
      }
      if (addressEl) addressEl.classList.toggle('is-visible', isAddress);
      container.classList.toggle('shipping-calc--showing-address', isAddress);
    }

    function showError(msg) {
      errorEl.textContent = msg;
      errorEl.classList.add('is-visible');
    }

    function hideError() {
      errorEl.classList.remove('is-visible');
    }

    function showLoading(show) {
      if (loading) loading.classList.toggle('is-visible', show);
      if (clearBtn) clearBtn.classList.toggle('is-visible', !show && input.value.length > 0);
      if (submitBtn) submitBtn.toggleAttribute('disabled', show);
    }

    function clearResults() {
      resultsEl.classList.remove('is-visible');
      resultsEl.innerHTML = '';
      hideError();
    }

    function prepareForNextCep() {
      clearResults();
      clearAddress();
      hideError();
      setState('input');
      localStorage.removeItem(scopedStorageKey);
    }

    function selectCepForOverwrite() {
      if (!editableCep || !input.value) return;

      requestAnimationFrame(function () {
        try {
          input.setSelectionRange(0, input.value.length);
        } catch (e) {
          if (typeof input.select === 'function') input.select();
        }
      });
    }

    function renderResults(data, requestAmountCents) {
      clearResults();
      clearAddress();

      const responseEntryWithAddress = getShippingResponseEntries(data).find(function (entry) {
        return entry?.address;
      });
      const address = responseEntryWithAddress?.address;

      if (address && addressText) {
        addressText.innerHTML = '';

        if (address.street) {
          const street = document.createElement('span');
          street.textContent = String(address.street);
          addressText.appendChild(street);
        }

        if (address.neighborhood || address.cep) {
          const metaLine = document.createElement('span');
          metaLine.className = 'shipping-calc__address-meta';

          if (address.neighborhood) {
            const neighborhood = document.createElement('span');
            neighborhood.className = 'shipping-calc__address-neighborhood';
            neighborhood.textContent = String(address.neighborhood);
            metaLine.appendChild(neighborhood);
          }

          if (address.cep) {
            const cep = document.createElement('span');
            cep.className = 'shipping-calc__address-cep';
            cep.textContent = String(address.cep);
            metaLine.appendChild(cep);
          }

          addressText.appendChild(metaLine);
        }

        setState('address');

        if (editableCep && document.activeElement === input) {
          selectCepForOverwrite();
        }
      }

      const options = normalizeOptions(data);
      if (!options.length) {
        showError('Nenhuma opcao de frete disponivel para este CEP.');
        return;
      }

      const FREE_LABEL = 'Gr\u00e1tis';
      const normalizedRequestAmountCents = Math.max(0, Math.round(toNumber(requestAmountCents, 0)));
      const reachedFreeShippingThreshold =
        freeShippingThresholdCents > 0 && normalizedRequestAmountCents >= freeShippingThresholdCents;

      options.forEach(function (opt) {
        const optionEl = document.createElement('div');
        optionEl.className = 'shipping-calc__option';

        const infoEl = document.createElement('div');
        infoEl.className = 'shipping-calc__option-info';

        const labelEl = document.createElement('span');
        labelEl.className = 'shipping-calc__option-label';
        labelEl.textContent = opt.label;

        infoEl.appendChild(labelEl);

        const businessDays = formatBusinessDays(opt.days);
        if (businessDays) {
          const daysEl = document.createElement('span');
          daysEl.className = 'shipping-calc__option-days';
          daysEl.textContent = businessDays;
          infoEl.appendChild(daysEl);
        }

        const priceEl = document.createElement('div');
        const basePrice = Math.max(0, toNumber(opt.originalPrice, opt.price));
        const shouldShowThresholdFree = reachedFreeShippingThreshold && basePrice > 0;

        if (shouldShowThresholdFree) {
          priceEl.className = 'shipping-calc__option-price is-threshold-free';

          const oldPriceEl = document.createElement('span');
          oldPriceEl.className = 'shipping-calc__option-price-old';
          oldPriceEl.textContent = formatCurrency(basePrice);

          const freePriceEl = document.createElement('span');
          freePriceEl.className = 'shipping-calc__option-price-free';
          freePriceEl.textContent = FREE_LABEL;

          priceEl.appendChild(oldPriceEl);
          priceEl.appendChild(freePriceEl);
        } else {
          priceEl.className = 'shipping-calc__option-price' + (opt.price === 0 ? ' is-free' : '');
          priceEl.textContent = opt.price === 0 ? FREE_LABEL : formatCurrency(opt.price);
        }

        optionEl.appendChild(infoEl);
        optionEl.appendChild(priceEl);
        resultsEl.appendChild(optionEl);
      });

      renderSubtleDisclaimer();
      resultsEl.classList.add('is-visible');
    }

    async function calculate(cep) {
      const cleanCep = String(cep || '').replace(/\D/g, '');
      if (cleanCep.length !== 8) {
        showError('CEP invalido. Digite 8 digitos.');
        return;
      }

      hideError();
      showLoading(true);
      clearResults();

      try {
        const payload = buildItemPayload(cleanCep, getQuantity());
        const requestAmountCents = Math.max(0, Math.round(toNumber(payload?.amount, 0) * 100));
        debugLog('request_payload', {
          cep: cleanCep,
          variant: activeVariant,
          requestAmountCents: requestAmountCents,
          payload: payload,
        });
        const mainData = await requestShipping(payload);
        debugLog('response_payload', mainData);

        if (mainData?.error) {
          showError(mainData.error);
          showLoading(false);
          return;
        }

        localStorage.setItem(scopedStorageKey, cleanCep);
        renderResults(mainData, requestAmountCents);
      } catch (e) {
        debugLog('request_error', {
          message: e?.message || String(e),
        });
        showError('Nao foi possivel calcular o frete. Tente novamente.');
      }

      showLoading(false);
    }

    input.addEventListener('input', function () {
      const raw = input.value;
      input.value = maskCEP(raw);

      if (editableCep && resultsEl.classList.contains('is-visible')) {
        clearResults();
        clearAddress();
        localStorage.removeItem(scopedStorageKey);
      }

      if (clearBtn) clearBtn.classList.toggle('is-visible', input.value.length > 0);
      hideError();

      const digits = input.value.replace(/\D/g, '');
      if (digits.length === 8) {
        calculate(digits);
      }
    });

    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        calculate(input.value);
      }
    });

    if (editableCep) {
      input.addEventListener('focus', function () {
        if (addressEl && addressEl.classList.contains('is-visible')) {
          selectCepForOverwrite();
        }
      });

      input.addEventListener('click', function () {
        if (addressEl && addressEl.classList.contains('is-visible')) {
          selectCepForOverwrite();
        }
      });
    }

    if (submitBtn) {
      submitBtn.addEventListener('click', function () {
        calculate(input.value);
      });
    }

    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        input.value = '';
        clearBtn.classList.remove('is-visible');
        prepareForNextCep();
        input.focus();
      });
    }

    if (changeCepBtn) {
      changeCepBtn.addEventListener('click', function () {
        if (editableCep) {
          input.focus();
          selectCepForOverwrite();
          return;
        }

        prepareForNextCep();
        input.value = '';
        input.focus();
      });
    }

    if (inputWrap) {
      inputWrap.addEventListener('click', function () {
        input.focus();
      });
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && sectionRoot) {
      subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        const eventSectionId = event?.data?.sectionId;
        if (!eventSectionId) return;
        if (sectionRoot.id !== 'MainProduct-' + eventSectionId) return;

        const normalized = normalizeVariant(event?.data?.variant);
        if (normalized) {
          activeVariant = normalized;
          if (normalized.id) {
            container.dataset.productVariantId = String(normalized.id);
          }
          if (normalized.sku) {
            container.dataset.productVariantSku = String(normalized.sku);
          }
          if (normalized.name) {
            container.dataset.productVariantName = String(normalized.name);
          }
          if (Number.isFinite(normalized.unitPrice) && normalized.unitPrice > 0) {
            container.dataset.productPrice = String(normalized.unitPrice);
          }
          if (Number.isFinite(normalized.weightKg)) {
            container.dataset.productWeight = String(normalized.weightKg);
          }
          if (Number.isFinite(normalized.lengthCm)) {
            container.dataset.productLength = String(normalized.lengthCm);
          }
          if (Number.isFinite(normalized.widthCm)) {
            container.dataset.productWidth = String(normalized.widthCm);
          }
          if (Number.isFinite(normalized.heightCm)) {
            container.dataset.productHeight = String(normalized.heightCm);
          }
        } else {
          activeVariant = readVariantFromDom();
        }

        debugLog('variant_change', {
          variant: activeVariant,
        });

        const cepDigits = input.value.replace(/\D/g, '');
        if (cepDigits.length === 8 && resultsEl.classList.contains('is-visible')) {
          calculate(cepDigits);
        }
      });
    }

    const savedCep = localStorage.getItem(scopedStorageKey);
    if (savedCep && savedCep.length === 8) {
      input.value = maskCEP(savedCep);
      if (clearBtn) clearBtn.classList.add('is-visible');
      calculate(savedCep);
    }

    debugLog('init_variant', {
      variant: activeVariant,
      productDefaults: productDefaults,
    });
  }

  function init() {
    document.querySelectorAll('[data-shipping-calculator]').forEach(initCalculator);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.theoricInitShippingCalculator = function (container) {
    if (container) {
      initCalculator(container);
    } else {
      init();
    }
  };
})();
